import React, { useState, useRef, useEffect } from 'react';
import Search from '../search/search';
import { DropdownContainer } from './customDropdown.style';
import Heading from '../typography/index';
import { TableBodyCellColumn } from '../../constants/style/table.styled';
import { RadioButtonWrapper } from '../radio-button/radioButton.styled';
import FlashMessage from '../flash-message';
import { type UserType } from '../../types/common';
import { isEmpty, isNull } from 'lodash';
import CheckFieldLevelPermissions from '../../utils/permission/checkFieldLevelPermission';
import { COLORS } from '../../constants/style/color';
import { getParticipantDisplayName } from '../../utils/participantDisplayName';
interface CustomDropdownType {
  errorMessage?: string;
  title: string;
  placeholder?: string;
  searchPlaceholder?: string;
  data?: UserType[] | any;
  checkedList?: string[];
  loading?: boolean;
  handleUser: (userInfo: UserType) => void;
  handleSubmit?: any;
  cssStyles?: Record<string, string | number | boolean>;
  toggleItems?: Record<string, string | number | boolean>;
  // close?: boolean;
  // showCheckbox?: boolean;
  // toggleSelected?: boolean;
  disabled?: boolean;
  touchedValue?: boolean;
  fieldLabelName?: string;
  searchInput?: (value: string) => void;
  isEdit?: boolean;
  id?: string;
  onClear?: () => void;
  virtualize?: boolean;
}

const CustomDropdown = ({
  errorMessage,
  title,
  placeholder,
  searchPlaceholder,
  data,
  loading,
  checkedList = [],
  handleUser,
  handleSubmit,
  cssStyles,
  toggleItems,
  touchedValue,
  searchInput,
  disabled,
  fieldLabelName,
  isEdit,
  id,
  onClear,
}: CustomDropdownType): JSX.Element => {
  const fieldPermission = CheckFieldLevelPermissions(fieldLabelName, isEdit, disabled);
  const isFieldDisabled = fieldLabelName != null && !isNull(fieldPermission);

  const [search, setSearch] = useState<string>('');
  const [approvalUserList, setApprovalUserList] = useState<UserType[] | undefined | null>(null);
  const [show, setShow] = useState(false);
  const [toggleData, setToggleData] = useState<Record<string, string | number | boolean>>({
    showSearch: true,
    showTotal: true,
    showCheckbox: true,
    showErrorMessage: true,
    close: false,
    shuffleName: false,
    searchable: false,
  });
  const [styles, setStyles] = useState<Record<string, string | number | boolean>>({
    column: 2,
    verticalGap: 4,
  });

  useEffect(() => {
    if (data != null) {
      setApprovalUserList(data);
    }
  }, [data]);

  useEffect(() => {
    setToggleData({ ...toggleData, ...toggleItems });
    setStyles({ ...styles, ...cssStyles });
  }, [toggleItems, cssStyles]);

  const handleShow = (): void => {
    setShow(!show);
  };

  const handleSearch = (value: string): void => {
    const filteredData = data?.filter((item: any) =>
      item.label.toLowerCase().includes(value.toLowerCase())
    );
    toggleData?.searchable === false && setApprovalUserList(filteredData);
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: Event): void {
      if (
        wrapperRef.current != null &&
        !wrapperRef.current.contains(event.target as Node) &&
        show
      ) {
        const targetElement: any = event.target;
        const labelElement = targetElement.querySelector('label');
        const planApprovalText = labelElement.textContent;
        handleSubmit?.(planApprovalText);
        setShow(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef, show]);

  const getOptionLabel = (item: UserType): string =>
    toggleData?.shuffleName ? getParticipantDisplayName(item) || item.label || '' : item.label;

  return (
    <div className='formField'>
      <DropdownContainer
        className={`${show ? 'active' : ''}`}
        padding={styles?.padding}
        outerPadding={styles?.outerPadding}
        height={styles?.height}
        width={styles?.width}
        contentHeight={styles?.contentHeight}>
        <div
          className={`dropdownContainer ${
            (disabled ?? false) ? 'disabled' : ''
          } ${show ? 'active' : ''}`}>
          <label htmlFor={id}>{title}</label>
          <button
            type='button'
            className={`dropdownButton ${
              isFieldDisabled
                ? 'disabled-button'
                : (disabled ?? false)
                ? 'disabled-button'
                : ''
            }  ${Boolean(errorMessage) && Boolean(touchedValue) ? 'is-invalid' : ''}`}
            onClick={() => {
              if (disabled) return;
              handleShow();
            }}
            id={id}>
            <span
              className={`${
                toggleData?.displaySelected != null &&
                toggleData?.displaySelected !== false &&
                (checkedList?.length > 0 || !isEmpty(placeholder))
                  ? 'placeholderTitle'
                  : 'dropdownTitle'
              } text-truncate `}>
              {isFieldDisabled
                ? placeholder !== null
                  ? placeholder
                  : 'xxxxx'
                : toggleData?.displaySelected != null && toggleData?.displaySelected !== false
                ? checkedList?.length > 0
                  ? `${checkedList?.length} selected`
                  : !isEmpty(placeholder)
                  ? placeholder
                  : 'Select'
                : !isEmpty(placeholder)
                ? placeholder
                : 'Select'}
            </span>
            {(checkedList?.length > 0 ||
              (!isEmpty(placeholder) && toggleData?.showCheckbox === false)) &&
            !disabled &&
            !isFieldDisabled ? (
              <span
                className='dropdown-clear'
                onClick={(e) => {
                  e.stopPropagation();
                  if (onClear) {
                    onClear();
                  } else {
                    // Handle clear internally by sending null value
                    handleUser({ value: null, label: null } as any);
                  }
                }}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 4px',
                }}>
                <span
                  className='material-symbols-rounded'
                  style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  close
                </span>
              </span>
            ) : (
              <span className='dropdown-arrow'>
                <span className='material-symbols-rounded'>arrow_drop_down</span>
              </span>
            )}
          </button>
          {show && (
            <div className='dropdownListContainer' ref={wrapperRef}>
              <div className='dropdownListWrapper'>
                {toggleData?.showSearch != null && toggleData?.showSearch !== false && (
                  <Search
                    searchValue={search}
                    placeholder={searchPlaceholder ?? 'Search'}
                    onSearch={(value: string) => {
                      handleSearch(value);
                      setSearch(value);
                      toggleData?.searchable === true && searchInput && searchInput(value);
                    }}
                  />
                )}
                {toggleData?.showTotal != null &&
                  toggleData?.showTotal !== false &&
                  data != null && (
                    <Heading
                      variant='body-xs'
                      title={
                        checkedList?.length > 0
                          ? `${checkedList?.length} participant${
                              checkedList.length > 1 ? 's' : ''
                            } selected out of ${data?.length}`
                          : `Total ${data?.length} participants`
                      }
                      className='selectedDropdownListInfo'
                    />
                  )}
                {loading ? (
                  <div className='flex justify-center mt-8'>
                    <Heading title={'Loading...'} variant='h6' color={COLORS?.SpanishGray} />
                  </div>
                ) : (
                  <div
                    className={`dropdownSearchContainer ${
                      toggleData?.showTotal != null &&
                      toggleData?.showTotal !== false &&
                      checkedList.length === 0
                        ? 'containerSpace'
                        : ''
                    }`}>
                    <div
                      className={`grid sm:grid-cols-${String(
                        styles?.column ?? ''
                      )} md:grid-cols-${String(styles?.column ?? '')} gap-y-${String(
                        styles?.verticalGap ?? ''
                      )} gap-x-5`}>
                      {!(loading ?? false) &&
                        approvalUserList?.map((item, index) => (
                          <div className='dropdownSearchItem' key={`dropdownSearchItem-${index}`}>
                            {toggleData?.showCheckbox != null &&
                              toggleData?.showCheckbox !== false && (
                                <RadioButtonWrapper
                                  label={getOptionLabel(item)}
                                  className='checkboxWrapper'
                                  id={item.value}
                                  onClick={() => {
                                    !(disabled ?? false) && handleUser(item);
                                  }}>
                                  <input
                                    type='checkbox'
                                    id={item.value}
                                    disabled={disabled}
                                    checked={checkedList.includes(item.value)}
                                  />
                                </RadioButtonWrapper>
                              )}
                            <label
                              htmlFor={item.value}
                              className={`dropdownSearchItem-label ${
                                !(
                                  (item?.employeeId != null && item?.employeeId !== '') ||
                                  (item.department != null &&
                                    item.department !== '' &&
                                    item.title != null &&
                                    item.title !== '')
                                )
                                  ? 'mb-0'
                                  : ''
                              }`}
                              onClick={() => {
                                toggleData?.close != null &&
                                  toggleData?.close !== false &&
                                  setShow(false);
                                !(disabled ?? false) && handleUser(item);
                              }}>
                              <TableBodyCellColumn wrap={true}>
                                <div className={`cellTitle`}>
                                  {getOptionLabel(item)}
                                </div>
                                {((item.employeeId != null && item.employeeId !== '') ||
                                  (item.department != null &&
                                    item.department !== '' &&
                                    item.title != null &&
                                    item.title !== '')) && (
                                  <>
                                    <div className='cellSubtitle'>
                                      {item.department != null
                                        ? `${item.department} | ${item.title ?? ''}`
                                        : ''}
                                    </div>
                                    <div className='cellSubtitle'>
                                      {item.employeeId != null ? `#${item.employeeId}` : ''}
                                    </div>
                                  </>
                                )}
                              </TableBodyCellColumn>
                            </label>
                          </div>
                        ))}
                    </div>
                    {!(loading ?? false) &&
                      approvalUserList != null &&
                      approvalUserList.length === 0 && (
                        <FlashMessage size='md' message='No Data Found' type='info' />
                      )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {toggleData?.showErrorMessage != null &&
          toggleData?.showErrorMessage !== false &&
          Boolean(errorMessage) &&
          Boolean(touchedValue) && <div className='error-message'>{errorMessage}</div>}
      </DropdownContainer>
    </div>
  );
};

export default CustomDropdown;
